import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer,
} from 'recharts';
import { api } from '../hooks/useApi';
import Icon from '../components/Icon';
import './EtatResultats.css';

// ─── CONSTANTES ──────────────────────────────────────────────────────────────

const ANNEE_COURANTE = new Date().getFullYear();
const MOIS_COURTS    = ['Jan','Fév','Mar','Avr','Mai','Jun','Jul','Aoû','Sep','Oct','Nov','Déc'];

function fmt$(n, forcer = false) {
  if (n == null && !forcer) return '—';
  const v = n ?? 0;
  const abs = Math.abs(v).toLocaleString('fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return (v < 0 ? '-' : '') + abs + ' $';
}

function ecartClass(v) {
  if (v == null) return '';
  return v > 0 ? 'er-pos' : v < 0 ? 'er-neg' : '';
}

// ─── LIGNE ÉTAT RÉSULTATS ─────────────────────────────────────────────────────
function Ligne({ label, fy, prev, niveau = 0, total = false, gras = false }) {
  const ecart = fy != null && prev != null ? fy - prev : null;
  return (
    <tr className={`er-ligne ${total ? 'er-total' : ''} ${gras ? 'er-gras' : ''}`}>
      <td className="er-label" style={{ paddingLeft: `${12 + niveau * 16}px` }}>{label}</td>
      <td className={`er-num ${fy < 0 ? 'er-neg' : fy > 0 ? 'er-pos' : ''}`}>{fmt$(fy)}</td>
      <td className="er-num er-prev">{fmt$(prev)}</td>
      <td className={`er-num ${ecartClass(ecart)}`}>{ecart != null ? (ecart >= 0 ? '+' : '') + fmt$(ecart) : '—'}</td>
    </tr>
  );
}

function Separateur() {
  return <tr className="er-sep"><td colSpan={4}><div /></td></tr>;
}

function SectionTitre({ label }) {
  return (
    <tr className="er-section">
      <td colSpan={4}>{label}</td>
    </tr>
  );
}

// ─── COMPOSANT PRINCIPAL ─────────────────────────────────────────────────────

export default function EtatResultats() {
  const [onglet, setOnglet]     = useState('resultats');
  const [annee, setAnnee]       = useState(String(ANNEE_COURANTE));
  const [data, setData]         = useState(null);
  const [bilan, setBilan]       = useState(null);
  const [cloture, setCloture]   = useState(null);
  const [fiches, setFiches]     = useState({ LCU: null, ELA: null });
  const [avancesResume, setAvancesResume] = useState(null);
  const [loading, setLoading]   = useState(false);
  const [exporting, setExporting] = useState(false);
  const [toast, setToast]       = useState('');
  const [passifs, setPassifs]   = useState('');

  const charger = useCallback(async (an) => {
    setLoading(true);
    try {
      const [res, bil, clot, ficheLcu, ficheEla, avances] = await Promise.all([
        api.get(`/etats/resultats/${an}`),
        api.get(`/etats/bilan/${an}`),
        api.get(`/etats/cloture/${an}`),
        api.get(`/etats/fiche-fiscale/LCU/${an}`),
        api.get(`/etats/fiche-fiscale/ELA/${an}`),
        api.get(`/avances/resume/${an}`),
      ]);
      setData(res);
      setBilan(bil);
      setCloture(clot);
      setFiches({ LCU: ficheLcu, ELA: ficheEla });
      setAvancesResume(avances);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { charger(annee); }, [annee, charger]);

  // ─── Export Excel ─────────────────────────────────────────────────────────
  async function exporterExcel() {
    setExporting(true);
    try {
      const token = localStorage.getItem('escamel_token');
      const resp  = await fetch(`/api/etats/export-excel/${annee}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error('Erreur génération Excel');
      const blob     = await resp.blob();
      const url      = URL.createObjectURL(blob);
      const a        = document.createElement('a');
      a.href         = url;
      a.download     = `Escamel_FY${annee}_Comptabilite.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setToast(`Escamel_FY${annee}_Comptabilite.xlsx généré — Prêt à envoyer à votre comptable`);
      setTimeout(() => setToast(''), 5000);
    } catch (e) {
      alert(e.response?.data?.error || 'Erreur export Excel');
    } finally {
      setExporting(false);
    }
  }

  const prev = data?.annee_precedente;

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="er-page">

      {/* ── HEADER ──────────────────────────────────────────────────────── */}
      <div className="er-header">
        <div className="er-header-text">
          <h1 className="er-titre">Comptabilité</h1>
          <p className="er-sous-titre muted">Escamel Artisans du Fumoir S.E.N.C. — NEQ 3381552556</p>
        </div>
        <div className="er-header-right">
          <select
            className="er-select"
            value={annee}
            onChange={e => setAnnee(e.target.value)}
          >
            {[ANNEE_COURANTE, ANNEE_COURANTE - 1, ANNEE_COURANTE - 2].map(a => (
              <option key={a} value={String(a)}>FY{a}</option>
            ))}
          </select>
          <button className="btn btn-primary" onClick={exporterExcel} disabled={exporting}>
            <Icon name="telecharger" size={14} />
            {exporting ? 'Génération…' : `Exporter FY${annee}`}
          </button>
        </div>
      </div>

      {/* ── TOAST ───────────────────────────────────────────────────────── */}
      {toast && (
        <div className="er-toast"><Icon name="succes" size={14} /> {toast}</div>
      )}

      {/* ── ONGLETS ─────────────────────────────────────────────────────── */}
      <div className="er-onglets">
        <button className={`er-tab ${onglet === 'resultats' ? 'active' : ''}`} onClick={() => setOnglet('resultats')}>État des résultats</button>
        <button className={`er-tab ${onglet === 'bilan'    ? 'active' : ''}`} onClick={() => setOnglet('bilan')}>Bilan</button>
        <button className={`er-tab ${onglet === 'cloture'  ? 'active' : ''}`} onClick={() => setOnglet('cloture')}>Clôture d'année</button>
      </div>

      {loading && (
        <div className="er-loading">
          <div className="skeleton" style={{ width: '100%', height: 48 }} />
          <div className="skeleton" style={{ width: '100%', height: 300 }} />
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════
          ONGLET ÉTAT DES RÉSULTATS
      ════════════════════════════════════════════════════════════════ */}
      {!loading && onglet === 'resultats' && data && (
        <>
          <div className="card er-table-card">
            <div className="er-table-header">
              <span className="er-table-titre">ÉTAT DES RÉSULTATS — FY{annee}</span>
              <span className="muted" style={{ fontSize: '11px', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Comptabilité de caisse</span>
            </div>
            <table className="er-table">
              <thead>
                <tr>
                  <th></th>
                  <th>FY{annee}</th>
                  <th className="er-prev">FY{data.annee_precedente?.annee}</th>
                  <th>Écart</th>
                </tr>
              </thead>
              <tbody>
                <SectionTitre label="REVENUS" />
                <Ligne label="Ventes WooCommerce"    fy={data.revenus.woocommerce}     prev={prev?.revenus?.woocommerce}    niveau={1} />
                <Ligne label="Ventes directes"       fy={data.revenus.ventes_directes} prev={prev?.revenus?.ventes_directes} niveau={1} />
                <Ligne label="TOTAL REVENUS"         fy={data.revenus.total}           prev={prev?.revenus?.total}          total gras />
                <Separateur />

                <SectionTitre label="DÉPENSES D'EXPLOITATION" />
                <Ligne label="Achat viande / intrants" fy={data.depenses_exploitation.viande_intrants} prev={prev?.depenses_exploitation?.viande_intrants} niveau={1} />
                <Ligne label="Total exploitation"      fy={data.depenses_exploitation.total}           prev={prev?.depenses_exploitation?.total}           total />
                <Separateur />

                <SectionTitre label="DÉPENSES ADMINISTRATIVES" />
                <Ligne label="Frais administratifs" fy={data.depenses_admin.frais_admin} prev={prev?.depenses_admin?.frais_admin} niveau={1} />
                <Ligne label="Repas/représentation — brut payé" fy={data.depenses_admin.repas_brut} prev={prev?.depenses_admin?.repas_brut} niveau={1} />
                <Ligne label="Repas/représentation — 50% déductible (ITA 67.1)" fy={data.depenses_admin.repas_deductible} prev={prev?.depenses_admin?.repas_deductible} niveau={1} />
                <Ligne label="Total admin"           fy={data.depenses_admin.total}       prev={prev?.depenses_admin?.total}       total />
                <Separateur />

                <SectionTitre label="ÉQUIPEMENT / R&D" />
                <Ligne label="Équipement"        fy={data.equipement_rd.equipement} prev={prev?.equipement_rd?.equipement} niveau={1} />
                <Ligne label="Total équipement"  fy={data.equipement_rd.total}      prev={prev?.equipement_rd?.total}      total />
                <Separateur />

                <SectionTitre label="AMORTISSEMENT (DPA)" />
                <Ligne label="Fumoir offset 500 gallons" fy={data.dpa.fumoir}   prev={prev?.dpa?.fumoir}   niveau={1} />
                <Ligne label="Food trailer 7x14"         fy={data.dpa.trailer}  prev={prev?.dpa?.trailer}  niveau={1} />
                <Ligne label="Total DPA"                 fy={data.dpa.total}    prev={prev?.dpa?.total}    total />
                <Separateur />

                <SectionTitre label="DÉPENSES PERSONNELLES DÉDUCTIBLES" />
                <Ligne label="Kilométrage LCU"          fy={data.depenses_perso.kilometrage_lcu} prev={prev?.depenses_perso?.kilometrage_lcu} niveau={1} />
                <Ligne label="Kilométrage ELA"          fy={data.depenses_perso.kilometrage_ela} prev={prev?.depenses_perso?.kilometrage_ela} niveau={1} />
                <Ligne label="Dépenses déductibles LCU" fy={data.depenses_perso.depenses_lcu}    prev={prev?.depenses_perso?.depenses_lcu}    niveau={1} />
                <Ligne label="Dépenses déductibles ELA" fy={data.depenses_perso.depenses_ela}    prev={prev?.depenses_perso?.depenses_ela}    niveau={1} />
                <Ligne label="Total dépenses perso"     fy={data.depenses_perso.total}           prev={prev?.depenses_perso?.total}           total />
                <tr>
                  <td colSpan="4" style={{ paddingTop: '0.4rem', paddingBottom: '0.75rem', fontSize: '0.78rem', fontStyle: 'italic', color: 'var(--muted)' }}>
                    * Ces dépenses sont déductibles personnellement par chaque associé sur sa déclaration T1 — elles ne sont pas incluses dans le résultat net de la société.
                  </td>
                </tr>
                <Separateur />

                <Ligne label="TOTAL DÉPENSES"           fy={data.total_depenses} prev={prev?.total_depenses} total gras />
                <tr className="er-benefice">
                  <td className="er-label er-gras">BÉNÉFICE NET AVANT IMPÔT</td>
                  <td className={`er-num er-gras er-xl ${data.benefice_net >= 0 ? 'er-pos' : 'er-neg'}`}>{fmt$(data.benefice_net)}</td>
                  <td className="er-num er-prev">{fmt$(prev?.benefice_net)}</td>
                  <td className={`er-num er-gras ${ecartClass(prev ? data.benefice_net - prev.benefice_net : null)}`}>
                    {prev ? ((data.benefice_net - prev.benefice_net) >= 0 ? '+' : '') + fmt$(data.benefice_net - prev.benefice_net) : '—'}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* ── Graphique mensuel ─────────────────────────────────────── */}
          <div className="card er-chart-card">
            <div className="er-chart-header">
              <span className="er-chart-icon"><Icon name="analytics" size={16} /></span>
              <span className="er-chart-titre">Revenus vs Dépenses par mois — FY{annee}</span>
            </div>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={data.par_mois.map(m => ({ ...m, mois_label: MOIS_COURTS[m.mois - 1] }))} barCategoryGap="25%">
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="mois_label" tick={{ fill: 'var(--muted)', fontSize: 12 }} />
                <YAxis tick={{ fill: 'var(--muted)', fontSize: 11 }} tickFormatter={v => v.toFixed(0) + '$'} />
                <Tooltip
                  contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)' }}
                  formatter={(v, name) => [fmt$(v), name]}
                  labelStyle={{ color: 'var(--text)', fontWeight: 600 }}
                />
                <Legend wrapperStyle={{ color: 'var(--muted)', fontSize: 12 }} />
                <Bar dataKey="revenus"  name="Revenus"  fill="var(--accent)"  radius={[3,3,0,0]} />
                <Bar dataKey="depenses" name="Dépenses" fill="var(--danger)"  radius={[3,3,0,0]} />
                <Bar dataKey="net"      name="Net"      fill="var(--success)" radius={[3,3,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="er-fiscal-grid">
            {['LCU', 'ELA'].map((associe) => {
              const fiche = fiches[associe];
              const avances = avancesResume?.[associe];
              if (!fiche) return null;

              return (
                <div key={associe} className="card er-fiscal-card">
                  <div className="er-fiscal-card-header">
                    <h3 className="er-fiscal-title">Feuille fiscale {associe}</h3>
                    <p className="muted er-fiscal-subtitle">
                      Déductions personnelles et informations comptables FY{annee}
                    </p>
                  </div>

                  <div className="er-fiscal-section">
                    <div className="er-fiscal-section-label">Dépenses personnelles déductibles</div>
                    <div className="er-fiscal-line">
                      <span>Déplacements</span>
                      <strong>{fmt$(fiche.depenses_personnelles?.deplacements)}</strong>
                    </div>
                    <div className="er-fiscal-line">
                      <span>Bureau domicile</span>
                      <strong>{fmt$(fiche.depenses_personnelles?.bureau_domicile?.total)}</strong>
                    </div>
                    <div className="er-fiscal-line">
                      <span>Autres dépenses</span>
                      <strong>{fmt$(fiche.depenses_personnelles?.autres?.reduce((sum, row) => sum + (row.deduction || 0), 0))}</strong>
                    </div>
                    <div className="er-fiscal-total">
                      <span>Total déductions perso</span>
                      <strong>{fmt$(fiche.total_deductions_perso)}</strong>
                    </div>
                  </div>

                  <div className="er-fiscal-section">
                    <div className="er-fiscal-section-label">Avances de fonds en suspens</div>
                    <div className="er-fiscal-line">
                      <span>Total avancé</span>
                      <strong>{fmt$(avances?.total_avance)}</strong>
                    </div>
                    <div className="er-fiscal-line">
                      <span>Total remboursé</span>
                      <strong>{fmt$(avances?.total_rembourse)}</strong>
                    </div>
                    <div className="er-fiscal-total">
                      <span>Solde restant</span>
                      <strong className={(avances?.solde_en_suspens ?? 0) > 0 ? 'gold' : 'success'}>
                        {fmt$(avances?.solde_en_suspens)}
                      </strong>
                    </div>
                    <div className="er-fiscal-note muted">À titre informatif - non déductible</div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* ════════════════════════════════════════════════════════════════
          ONGLET BILAN
      ════════════════════════════════════════════════════════════════ */}
      {!loading && onglet === 'bilan' && bilan && (
        <div className="er-bilan-grid">
          {/* ACTIFS */}
          <div className="card er-bilan-card">
            <h3 className="er-bilan-titre gold">ACTIFS</h3>

            <div className="er-bilan-section">
              <div className="er-bilan-label muted">Actifs courants</div>
              <div className="er-bilan-ligne">
                <span>Inventaire produits finis</span>
                <span>{fmt$(bilan.actifs_courants.inventaire_produits_finis)}</span>
              </div>
              <div className="er-bilan-sous-total">
                <span>Total courants</span>
                <span>{fmt$(bilan.actifs_courants.total)}</span>
              </div>
            </div>

            <div className="er-bilan-section">
              <div className="er-bilan-label muted">Actifs long terme (FNACC)</div>
              {bilan.actifs_long_terme.detail.map(a => (
                <div key={a.description} className="er-bilan-ligne">
                  <span>{a.description}</span>
                  <span>{fmt$(a.fnacc)}</span>
                </div>
              ))}
              <div className="er-bilan-sous-total">
                <span>Total long terme</span>
                <span>{fmt$(bilan.actifs_long_terme.total)}</span>
              </div>
            </div>

            <div className="er-bilan-total">
              <span>TOTAL ACTIFS</span>
              <span className="gold">{fmt$(bilan.total_actifs)}</span>
            </div>
          </div>

          {/* PASSIFS + AVOIR */}
          <div>
            <div className="card er-bilan-card" style={{ marginBottom: 16 }}>
              <h3 className="er-bilan-titre">PASSIFS</h3>
              <div className="er-bilan-section">
                <div className="er-bilan-ligne">
                  <span>Prêt Desjardins PCA</span>
                  <input
                    type="number"
                    className="er-passif-input"
                    placeholder="Saisir montant"
                    value={passifs}
                    onChange={e => setPassifs(e.target.value)}
                  />
                </div>
              </div>
              <div className="er-bilan-total">
                <span>TOTAL PASSIFS</span>
                <span className="danger">{fmt$(parseFloat(passifs) || 0)}</span>
              </div>
            </div>

            <div className="card er-bilan-card">
              <h3 className="er-bilan-titre">AVOIR DES ASSOCIÉS</h3>
              {(() => {
                const lcu = bilan.avoir_associes?.LCU;
                const ela = bilan.avoir_associes?.ELA;
                return (
                  <>
                    <div className="er-bilan-section">
                      <div className="er-bilan-label muted">LCU</div>
                      <div className="er-bilan-ligne">
                        <span>Actifs propres (Fumoir, etc.)</span>
                        <span>{fmt$(lcu?.actifs_propres)}</span>
                      </div>
                      <div className="er-bilan-ligne">
                        <span>Part actifs Escamel (50%)</span>
                        <span>{fmt$(lcu?.part_actifs_escamel)}</span>
                      </div>
                      <div className="er-bilan-ligne">
                        <span>Part résultat net (50%)</span>
                        <span>{fmt$(lcu?.part_resultat_net)}</span>
                      </div>
                      <div className="er-bilan-sous-total">
                        <span>Total LCU</span>
                        <span className={(lcu?.total ?? 0) >= 0 ? 'success' : 'danger'}>{fmt$(lcu?.total)}</span>
                      </div>
                    </div>
                    <div className="er-bilan-section">
                      <div className="er-bilan-label muted">ELA</div>
                      <div className="er-bilan-ligne">
                        <span>Actifs propres</span>
                        <span>{fmt$(ela?.actifs_propres)}</span>
                      </div>
                      <div className="er-bilan-ligne">
                        <span>Part actifs Escamel (50%)</span>
                        <span>{fmt$(ela?.part_actifs_escamel)}</span>
                      </div>
                      <div className="er-bilan-ligne">
                        <span>Part résultat net (50%)</span>
                        <span>{fmt$(ela?.part_resultat_net)}</span>
                      </div>
                      <div className="er-bilan-sous-total">
                        <span>Total ELA</span>
                        <span className={(ela?.total ?? 0) >= 0 ? 'success' : 'danger'}>{fmt$(ela?.total)}</span>
                      </div>
                    </div>
                    <div className="er-bilan-total">
                      <span>TOTAL AVOIR</span>
                      <span className={(bilan.avoir_associes?.total_avoir ?? 0) >= 0 ? 'success' : 'danger'}>
                        {fmt$(bilan.avoir_associes?.total_avoir)}
                      </span>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════
          ONGLET CLÔTURE
      ════════════════════════════════════════════════════════════════ */}
      {!loading && onglet === 'cloture' && cloture && (
        <div className="card er-cloture-card">
          <div className="er-cloture-header">
            <h2 className="er-cloture-titre">CLÔTURE FY{annee} — 31 décembre {annee}</h2>
          </div>

          <div className="er-checklist">
            <CheckItem
              ok={cloture.journal.complet}
              label="Journal général complet"
              detail={`${cloture.journal.nb_transactions} transaction${cloture.journal.nb_transactions !== 1 ? 's' : ''} enregistrée${cloture.journal.nb_transactions !== 1 ? 's' : ''}`}
              lien="/comptabilite/journal"
            />
            <CheckItem
              ok={cloture.dpa.validee}
              label="DPA calculée et validée"
              detail={cloture.dpa.calculee ? `Total DPA réclamée : ${fmt$(cloture.dpa.total)}` : 'Non calculée'}
              lien="/comptabilite/actifs"
            />
            <CheckItem
              ok={cloture.kilometrage_lcu.complet}
              label="Dépenses kilométrage LCU complètes"
              detail={`${cloture.kilometrage_lcu.km.toFixed(1)} km — ${fmt$(cloture.kilometrage_lcu.deduction)}`}
              lien="/comptabilite/depenses-perso"
            />
            <CheckItem
              ok={cloture.kilometrage_ela.complet}
              label="Dépenses kilométrage ELA complètes"
              detail={`${cloture.kilometrage_ela.km.toFixed(1)} km — ${fmt$(cloture.kilometrage_ela.deduction)}`}
              lien="/comptabilite/depenses-perso"
            />
            <CheckItem
              ok={cloture.depenses_lcu.complet}
              label="Dépenses déductibles LCU saisies"
              detail={`${cloture.depenses_lcu.nb_items} item(s) • ${fmt$(cloture.depenses_lcu.deduction)}`}
              lien="/comptabilite/depenses-perso"
            />
            <CheckItem
              ok={cloture.depenses_ela.complet}
              label="Dépenses déductibles ELA saisies"
              detail={`${cloture.depenses_ela.nb_items} item(s) • ${fmt$(cloture.depenses_ela.deduction)}`}
              lien="/comptabilite/depenses-perso"
            />
            <CheckItem
              ok={cloture.inventaire.complet}
              label="Inventaire produits finis valorisé"
              detail={`Valeur au 31 déc : ${fmt$(cloture.inventaire.valeur)}`}
              lien="/inventaire/produits"
            />
            <div className="er-check-item er-check-manual">
              <span className="er-check-icon er-check-pending"></span>
              <div>
                <div className="er-check-label">Export Excel envoyé au comptable</div>
                <div className="er-check-detail muted">À faire manuellement après génération</div>
              </div>
            </div>
          </div>

          <div className="er-cloture-footer">
            <button
              className="btn btn-primary er-btn-export"
              onClick={exporterExcel}
              disabled={exporting}
            >
              {exporting ? 'Génération en cours…' : `Générer le rapport final FY${annee}`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── CHECK ITEM ───────────────────────────────────────────────────────────────
function CheckItem({ ok, label, detail, lien }) {
  return (
    <div className="er-check-item">
      <span className={`er-check-icon ${ok ? 'er-check-ok' : 'er-check-ko'}`}>
        {ok ? <Icon name="check" size={12} /> : <Icon name="fermer" size={12} />}
      </span>
      <div className="er-check-content">
        <div className={`er-check-label ${ok ? '' : 'danger'}`}>{label}</div>
        <div className="er-check-detail muted">
          {detail}
          {!ok && lien && (
            <a href={lien} className="er-check-lien">Compléter</a>
          )}
        </div>
      </div>
    </div>
  );
}
